import React, { useState, useEffect, useMemo } from "react";
import { useTable, useFilters, usePagination, useSortBy } from "react-table";
import { connect } from "react-redux";
import { compose } from "redux";
import { Redirect } from "react-router-dom";
import { RiTableFill, RiTableLine, RiGridFill, RiGridLine } from "react-icons/ri";
import { PiCalendarDotsFill, PiCalendarDots } from "react-icons/pi";
import moment from "moment";
import CalendarView from "../components/Control/CalendarView";
import LOADER from "../assets/images/loader.svg";
import { getControls_Report, editControl, getControls_Search } from "../store/actions/riskActions";
import Popup from 'reactjs-popup';
import ControlOverlay from "../components/Control/ControlOverlay";
import TopBar from "../components/TopBar";
import Sidebar from "../components/Sidebar";
import ControlsHeader from "../components/Control/ControlsHeader";
import "../components/Control/controls.theme.css";


function AllControls(props) {
    const { token, controls, activeDepartmentId, getControlsReportFN, getControlsSearchFN, editControlFN } = props;

    const [loading, setLoading] = useState(false);
    const [flattenedControls, setFlattenedControls] = useState([]);
    const [selectedView, setSelectedView] = useState("table"); 
    const [viewMode, setViewMode] = useState("table"); 
    const [isControlOpen, setIsControlOpen] = useState(false);
    const [control_id, setControlId] = useState(null);
    const [risk_id, setRiskId] = useState(null);

    const [query, setQuery] = useState("");
    const [typeView, setTypeView] = useState("All");
    const [effView, setEffView] = useState("All");
    const [statusView, setStatusView] = useState("All");
    const [calendarDate, setCalendarDate] = useState(new Date());



    useEffect(() => {
        console.log(activeDepartmentId)
        if (activeDepartmentId === null) {
            setLoading(true);
            getControlsSearchFN(null).finally(() => setLoading(false));
            //console.log(controls);
        }
    }, [activeDepartmentId, getControlsSearchFN]);

    useEffect(() => {
        if (controls && controls?.length > 0) {
            //console.log("Controls before flat:" , controls);
            const allControls = controls
                .filter((control) =>
                    control.controls.some(
                        (c) =>
                            c.controlDetails !== null ||
                            c.controlType !== null ||
                            c.controlDefinition !== null
                    )

                )
                .flatMap((control) =>
                    control.controls.map((c) => ({
                        ...c,
                        riskId: control.riskId,
                        title: control.title,
                        owner: c.owner,
                        is_secondline: c.secondline
                    }))
                );
            setFlattenedControls(allControls);
        } else {
            setFlattenedControls([]);
        }
    }, [controls]);

    const closeCreation = async (data, control_id) => {
        console.log("Data:", data, "ID", control_id)

        setIsControlOpen(false)
        await updateServerData(data, control_id);
        getControlsSearchFN(null)

    }

    const updateServerData = async (data, control_id) => {


        const updatedData = { ...data };

        
        if (updatedData.StartDate) {
            const startDate = new Date(updatedData.StartDate);
            startDate.setDate(startDate.getDate() + 1);
            updatedData.StartDate = startDate.toISOString().split('T')[0]; 
        }

        if (updatedData.EndDate) {
            const endDate = new Date(updatedData.EndDate);
            endDate.setDate(endDate.getDate() + 1);
            updatedData.EndDate = endDate.toISOString().split('T')[0]; 
        }

        //console.log("CONTROL DATA AFTER ADJUSTMENT", updatedData);

        setLoading(true);
        await editControlFN(risk_id, control_id, updatedData);
        setLoading(false);

    }

    const compliancePillClass = (t) => {
        const map = {
            "NIST": "bg-blue-100 text-blue-800 border-blue-300",
            "COBIT": "bg-purple-100 text-purple-800 border-purple-300",
            "ISO 27001": "bg-emerald-100 text-emerald-800 border-emerald-300",
            "GDPR": "bg-amber-100 text-amber-900 border-amber-300",
        };
        return map[t] || "bg-gray-100 text-gray-800 border-gray-300";
    };
    const typePillClass = (t) => {
        const key = (t || "").toString().trim().toLowerCase();
        if (key === "preventive") return "pill-blue";
        if (key === "detective") return "pill-purple";
        if (key === "corrective") return "pill-orange";
        if (key === "mitigative") return "pill-red";
        if (key === "directive") return "pill-green";
        return "badge-soft-gray";
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Control Name",
                accessor: "controlDetails",
                width: 380,
                Cell: ({ value }) => (
                    <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{value}</div>
                ),
            },
            {
                Header: "Frameworks",
                accessor: "complianceTypes",
                sortType: (rowA, rowB, columnId) => {
                    const a = rowA.values[columnId];
                    const b = rowB.values[columnId];
                    const lenA = Array.isArray(a) ? a.length : (typeof a === "string" && a ? a.split(",").length : 0);
                    const lenB = Array.isArray(b) ? b.length : (typeof b === "string" && b ? b.split(",").length : 0);
                    return lenA - lenB; // ascending by count; we'll flip to desc first
                },
                sortDescFirst: true,
                Cell: ({ value }) => (
                    <div className="flex flex-wrap gap-1 justify-center">
                        {Array.isArray(value) && value.length > 0
                            ? value.map((c, i) => (
                                <span
                                    key={i}
                                    className="bg-gray-100 text-gray-700 border border-gray-300 px-2 py-0.5 rounded text-xs"
                                >
                                    {c}
                                </span>
                            ))
                            : <span className="text-gray-400 text-xs">None</span>}
                    </div>
                ),
            },
            {
                Header: "Type",
                accessor: "controlType",
                Cell: ({ value }) => (
                    <span className={`px-3 py-1 rounded-lg text-xs ${typePillClass(value)}`}>
                        {value || "-"}
                    </span>
                ),
            },
            {
                Header: "Effectiveness",
                accessor: "efficiencyLevel",
                width: 110,
                Cell: ({ value }) => {
                    const num = Number(value ?? 0);
                    const label = num >= 75 ? "High" : num >= 40 ? "Medium" : "Low";
                    const colorClass = num >= 75 ? "bg-green-700 text-white" : num >= 40 ? "bg-yellow-600 text-white" : "bg-red-700 text-white";
                    return (
                        <span className={`${colorClass} px-2 py-1 rounded text-xs font-medium`}>
                            {label}
                    </span>
                    );
                },
            },
            { Header: "Period", accessor: "controlPeriod" },
            { Header: "Risk Title", accessor: "title" },
            {
                Header: "Owner",
                accessor: (row) =>
                    `${row.owner?.firstName || ""} ${row.owner?.lastName || ""}`,
                id: "owner",
            },
        ],
        []
    );

    
    const columnWidth = {
        "Control ID": 40,
        "Control Name": 100, 
        Type: 120,
        Frameworks: 140,
        Findings: 70,
        Effectiveness: 90,
        Owner: 120,
        Period: 80,
    };

    // Filters + search
    const effBucket = (n) => {
        const v = Number(n ?? 0);
        if (v >= 75) return "high";
        if (v >= 40) return "medium";
        return "low";
    };
    const filteredControls = useMemo(() => {
        const q = (query || "").toLowerCase().trim();
        const norm = (v) => (v ?? "").toString().toLowerCase();
        const wantType = (typeView || "All").toLowerCase();
        const wantEff = (effView || "All").toLowerCase();
        const wantStatus = (statusView || "All").toLowerCase();
        const statusOf = (c) => {
            const now = new Date();
            const s = c.startDate ? new Date(c.startDate) : null;
            const e = c.endDate ? new Date(c.endDate) : null;
            if (e && e < now) return "closed";
            if (s && s > now) return "open";
            return "in progress";
        };
        return (flattenedControls || []).filter((c) => {
            if (q) {
                const hay = [
                    norm(c.controlID),
                    norm(c.controlDetails),
                    norm(c.controlDefinition),
                    norm(Array.isArray(c.complianceTypes) ? c.complianceTypes.join(",") : c.complianceTypesRaw || ""),
                    norm(c.owner?.firstName), norm(c.owner?.lastName),
                    norm(c.controlType),
                ].join(" ");
                if (!hay.includes(q)) return false;
            }
            if (wantType !== "all" && (c.controlType || "").toLowerCase() !== wantType) return false;
            if (wantEff !== "all" && effBucket(c.efficiencyLevel) !== wantEff) return false;
            if (wantStatus !== "all" && statusOf(c) !== wantStatus) return false;
            return true;
        });
    }, [flattenedControls, query, typeView, effView, statusView]);

    //console.log("flattened controls:", flattenedControls);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        gotoPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: filteredControls,
            initialState: { pageIndex: 0, pageSize: 10 },
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetFilters: false,
        },
        useFilters,
        useSortBy,
        usePagination
    );

    const renderPagination = () => (
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-6 py-3 mt-4">
            <div className="text-sm text-gray-600">
                {`Showing ${pageIndex * pageSize + 1} to ${Math.min(
                    (pageIndex + 1) * pageSize,
                    filteredControls?.length || 0
                )} of ${filteredControls?.length || 0} controls`}
            </div>
            <div className="flex items-center gap-2">
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="px-2 py-1 text-sm text-gray-600 disabled:opacity-40">{'«'}</button>
                <button onClick={() => previousPage()} disabled={!canPreviousPage} className="px-2 py-1 text-sm text-gray-600 disabled:opacity-40">Previous</button>
                <button onClick={() => gotoPage(pageIndex)} className="px-3 py-1 text-sm bg-black text-white rounded">{pageIndex + 1}</button>
                {Math.ceil((filteredControls?.length || 0) / pageSize) > pageIndex + 1 && (
                    <button onClick={() => gotoPage(pageIndex + 1)} className="px-3 py-1 text-sm text-gray-700 rounded hover:bg-gray-100">{pageIndex + 2}</button>
                )}
                <button onClick={() => nextPage()} disabled={!canNextPage} className="px-2 py-1 text-sm text-gray-600 disabled:opacity-40">Next</button>
                <button onClick={() => gotoPage(Math.max(0, Math.ceil((filteredControls?.length || 0) / pageSize) - 1))} disabled={!canNextPage} className="px-2 py-1 text-sm text-gray-600 disabled:opacity-40">{'»'}</button>
            </div>
            <div className="text-sm text-gray-600">
                10 per page
            </div>
        </div>
    );

    //console.log("RISK ID FOR CONTROL", controls.risk_id);

    return token ? (
        <div className="flex flex-col w-full min-h-screen overflow-x-hidden">
            <TopBar className="fixed top-0 left-0 right-0 z-10" />
            <div className="flex flex-row w-full overflow-x-hidden"> {/* Prevent horizontal scrolling */}
                <Sidebar className="fixed top-0 left-0 h-full pt-[64px] z-0" /> {/* Adjust the top to match TopBar height */}
                <div className="controls-container">
                    <div className="controls-inner w-full max-w-[1700px] mx-auto px-4">
                    {loading ? (
                        <img src={LOADER} alt="Loading..." className="spinner" />
                    ) : (
                        <>
                            <div className="px-0 pt-6 pb-2">
                                <div className="w-full flex items-center justify-between mb-3">
                                    <div className="">
                                        <h1 className="text-xl md:text-2xl font-medium tracking-wide text-[#0E0E0E]">
                                            {selectedView === "table" ? "Controls Management" : "Calendar View"}
                                        </h1>
                                        <p className="text-xs md:text-sm text-gray-600">
                                            {selectedView === "table" ? "Manage and monitor your security controls" : "View your controls schedule"}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-0 border border-gray-300 rounded-lg overflow-hidden">
                                        <button
                                            onClick={() => setSelectedView("table")}
                                            className={`flex items-center gap-2 border-r border-gray-300 px-3.5 py-1.5 text-sm font-medium transition ${
                                                selectedView === "table"
                                                    ? "bg-[#0E0E0E] text-white"
                                                    : "bg-white text-gray-600 hover:bg-gray-50"
                                            }`}
                                        >
                                            {selectedView === "table" ? (
                                                <RiGridFill size={16} />
                                            ) : (
                                                <RiGridLine size={16} />
                                            )}
                                            <span>Controls</span>
                                        </button>

                                        <button
                                            onClick={() => setSelectedView("calendar")}
                                            className={`flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium transition ${
                                                selectedView === "calendar"
                                                    ? "bg-[#0E0E0E] text-white"
                                                    : "bg-white text-gray-600 hover:bg-gray-50"
                                            }`}
                                        >
                                            {selectedView === "calendar" ? (
                                                <PiCalendarDotsFill size={16} />
                                            ) : (
                                                <PiCalendarDots size={16} />
                                            )}
                                            <span>Calendar</span>
                                        </button>
                                    </div>
                                </div>
                                {selectedView === "table" ? (
                                <ControlsHeader
                                    query={query}
                                    setQuery={setQuery}
                                    typeView={typeView}
                                    setTypeView={setTypeView}
                                    effView={effView}
                                    setEffView={setEffView}
                                    statusView={statusView}
                                    setStatusView={setStatusView}
                                        viewMode={viewMode}
                                        setViewMode={setViewMode}
                                        showToggle={true}
                                    />
                                ) : (
                                    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-2 mb-4 shadow-sm">
                                        <div className="flex items-center justify-end">
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => {
                                                        const newDate = moment(calendarDate).subtract(1, "month").toDate();
                                                        setCalendarDate(newDate);
                                                    }}
                                                    className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition"
                                                >
                                                    <span>Previous</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const newDate = moment(calendarDate).add(1, "month").toDate();
                                                        setCalendarDate(newDate);
                                                    }}
                                                    className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition"
                                                >
                                                    <span>Next</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {selectedView === "calendar" ? (
                                <div className="mt-12 flex justify-center">
                                    <div className="w-full max-w-[1700px]">
                                        <CalendarView 
                                            events={filteredControls} 
                                            currentDate={calendarDate}
                                            onNavigate={setCalendarDate}
                                        />
                                    </div>
                                </div>
                            ) : viewMode === "grid" ? (
                                <>
                                    <div className="mt-4">
                                        <div className="px-6 py-4 border-b border-gray-200 bg-white rounded-t-lg">
                                            <h3 className="text-gray-900">Controls List</h3>
                                            <p className="text-sm text-gray-500">
                                                {(filteredControls?.length ?? 0)} controls found
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 px-3 py-4 bg-white rounded-b-lg border border-gray-200 border-t-0 items-stretch">
                                            {page.map((row) => {
                                                prepareRow(row);
                                                const control = row.original;
                                                const num = Number(control.findingNumber ?? 0);
                                                const findingCls = isNaN(num) ? "" : (num === 0 ? "badge-green" : "badge-red");
                                                const effRaw = Number(control.efficiencyLevel ?? 0);
                                                const effLabel = effRaw >= 75 ? "High" : effRaw >= 40 ? "Medium" : "Low";
                                                const effColorClass = effRaw >= 75 ? "bg-green-700 text-white" : effRaw >= 40 ? "bg-yellow-600 text-white" : "bg-red-700 text-white";
                                                const ownerName = `${control.owner?.firstName || ""} ${control.owner?.lastName || ""}`.trim() || "-";
                                                return (
                                                    <div
                                                        key={control.controlID}
                                                        onClick={() => {
                                                            const matchingRisk = controls.find(risk =>
                                                                risk.controls.some(ctrl => ctrl.controlID === control.controlID)
                                                            );
                                                            if (matchingRisk) setRiskId(matchingRisk.riskId);
                                                            setControlId(control.controlID);
                                                            setTimeout(() => setIsControlOpen(true), 0);
                                                        }}
                                                        className="bg-white border border-gray-200 rounded-lg px-2 py-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex flex-col min-h-[280px]"
                                                    >
                                                        <div className="flex items-start justify-between mb-1.5">
                                                            <span className="text-xs text-gray-500">{`CTRL-${control.controlID ?? ""}`}</span>
                                                            {control.controlPeriod && (
                                                                <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-700">{control.controlPeriod}</span>
                                                            )}
                                                        </div>
                                                        <h4 className="font-medium text-gray-800 mb-2 line-clamp-2">{control.controlDetails || "-"}</h4>
                                                        {control.controlDefinition && (
                                                            <p className="text-xs text-gray-500 mb-2 line-clamp-2">{control.controlDefinition}</p>
                                                        )}
                                                        <div className="flex flex-col gap-1.5 mb-2">
                                                            <div className="flex items-center gap-1.5">
                                                                {control.controlType && (
                                                                    <span className={`px-2 py-1 rounded-lg text-xs ${typePillClass(control.controlType)}`}>
                                                                        {control.controlType}
                                                                    </span>
                                                                )}
                                                                <span className={`${effColorClass} px-1.5 py-1 rounded text-xs font-medium`}>{effLabel}</span>
                                                            </div>
                                                            <div className="flex flex-wrap gap-0.5 justify-start">
                                                                {Array.isArray(control.complianceTypes) && control.complianceTypes.length > 0
                                                                    ? control.complianceTypes.map((c, i) => (
                                                                        <span
                                                                            key={i}
                                                                            className="bg-gray-100 text-gray-700 border border-gray-300 px-1.5 py-0.5 rounded text-xs"
                                                                        >
                                                                            {c}
                                                                        </span>
                                                                    ))
                                                                    : <span className="text-gray-400 text-xs">None</span>}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col gap-1 pt-2 border-t border-gray-100 mt-auto">
                                                            <div>
                                                                <span className="text-xs text-gray-500">Owner: </span>
                                                                <span className="text-xs text-gray-700">{ownerName}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-500">Findings: </span>
                                                                <span className={`text-xs ${findingCls}`}>{isNaN(num) ? "-" : num}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-500">Last Reviewed: </span>
                                                                <span className="text-xs text-gray-700">
                                                                    {control.lastReviewDate 
                                                                        ? new Date(control.lastReviewDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.')
                                                                        : control.reviewDate
                                                                            ? new Date(control.reviewDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.')
                                                                            : "-"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {renderPagination()}
                                </>
                            ) : (
                                <>
                                    <div className="table-wrapper shadow-xl bg-white rounded-lg border border-gray-200 overflow-hidden" style={{ marginTop: "16px" }}>
                                        <div className="px-6 py-4 border-b border-gray-200">
                                            <h3 className="text-gray-900">Controls List</h3>
                                            <p className="text-sm text-gray-500">
                                                {(filteredControls?.length ?? 0)} controls found (Page {pageIndex + 1} of {Math.max(1, Math.ceil((filteredControls?.length || 0) / pageSize))})
                                            </p>
                                        </div>
                                        <table {...getTableProps()} className="main-table shadow-xl">
                                            <thead>
                                                {headerGroups.map((headerGroup) => {
                                                    
                                                    const cols = headerGroup.headers.reduce((map, col) => {
                                                        map[col.Header] = col;
                                                        return map;
                                                    }, {});
                                                    const order = ["Control Name", "Type", "Frameworks", "Findings", "Effectiveness", "Owner", "Period"];
                                                    const centerHeaders = ["Type", "Frameworks", "Findings", "Effectiveness", "Owner", "Period"];
                                                    return (
                                                        <tr
                                                            {...headerGroup.getHeaderGroupProps()}
                                                            className="border-b border-gray-300"
                                                        >
                                                            <th
                                                                className="px-3 py-2 text-left text-xs text-gray-700 font-semibold"
                                                                style={{ width: columnWidth["Control ID"] }}
                                                            >
                                                                Control ID
                                                            </th>
                                                            {order.map((name) => {
                                                                if (name === "Findings") {
                                                                    return (
                                                                        <th
                                                                            key="Findings"
                                                                            className="px-2 py-2 text-center text-xs text-gray-700 font-semibold"
                                                                        style={{ width: columnWidth["Findings"] }}
                                                                        >
                                                                            Findings
                                                                        </th>
                                                                    );
                                                                }
                                                                const column = cols[name];
                                                                if (!column) return null;
                                                                const isCenter = centerHeaders.includes(name);
                                                                return (
                                                                    <th
                                                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                                                        className={`cursor-pointer px-4 py-2 text-xs text-gray-700 font-semibold whitespace-nowrap ${isCenter ? "text-center" : "text-left"}`}
                                                                        style={{ width: columnWidth[name] }}
                                                                    >
                                                                        <div className={`flex items-center ${isCenter ? "justify-center" : ""}`}>
                                                                            {name}
                                                                            <span>
                                                                                {column.isSorted
                                                                                    ? column.isSortedDesc
                                                                                        ? " 🔽"
                                                                                        : " 🔼"
                                                                                    : ""}
                                                                            </span>
                                                                        </div>
                                                                    </th>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                })}
                                            </thead>

                                            <tbody {...getTableBodyProps()}>
                                                {page.map((row) => {
                                                    prepareRow(row);
                                                    return (
                                                        <tr
                                                            {...row.getRowProps({
                                                                onClick: () => {
                                                                    const matchingRisk = controls.find(risk =>
                                                                        risk.controls.some(ctrl => ctrl.controlID === row.original.controlID)
                                                                    );
                                                                    if (matchingRisk) setRiskId(matchingRisk.riskId);
                                                                    setControlId(row.original.controlID);
                                                                    setTimeout(() => setIsControlOpen(true), 0);
                                                                }
                                                            })}
                                                            className="group transition-colors duration-200 cursor-pointer"
                                                        >
                                                            <td
                                                                className="border-t border-gray-200 pr-2 pl-3 py-2 group-hover:bg-[#F8F8F8] whitespace-nowrap"
                                                                style={{ width: columnWidth["Control ID"] }}
                                                            >{`CTRL-${row.original.controlID ?? ""}`}</td>
                                                            {(() => {
                                                                
                                                                const cellMap = {};
                                                                row.cells.forEach((cell) => {
                                                                    cellMap[cell.column.Header] = cell;
                                                                });
                                                                const centerColumns = ["Type", "Frameworks", "Findings", "Effectiveness", "Owner", "Period"];
                                                                const renderCell = (cell) => {
                                                                    if (!cell) return null;
                                                                    
                                                                    if (/^(efficiency|controlEfficiency|Efficiency|efficiency_level|EfficiencyLevel)$/i.test(cell.column.id)) {
                                                                        const raw = typeof cell.value === "number" ? cell.value : Number(row.original.efficiencyLevel ?? row.original.controlEfficiency ?? row.original.efficiency ?? 0);
                                                                        const label = raw >= 75 ? "High" : raw >= 40 ? "Medium" : "Low";
                                                                        const colorClass = raw >= 75 ? "bg-green-700 text-white" : raw >= 40 ? "bg-yellow-600 text-white" : "bg-red-700 text-white";
                                                                        return (
                                                                            <td
                                                                                {...cell.getCellProps()}
                                                                                className="border-t border-gray-200 px-2 py-2 group-hover:bg-[#F8F8F8] whitespace-nowrap text-center"
                                                                                style={{ width: columnWidth["Effectiveness"] }}
                                                                            >
                                                                                <span className={`${colorClass} px-2 py-1 rounded text-xs font-medium`}>{label}</span>
                                                                            </td>
                                                                        );
                                                                    }
                                                                    
                                                                    if (cell.column.id === "controlDetails" || cell.column.id === "control_title" || cell.column.id === "ControlDetails" || cell.column.id === "controlTitle") {
                                                                        return (
                                                                            <td
                                                                                {...cell.getCellProps()}
                                                                                className="border-t border-gray-200 pl-2 pr-4 py-2 group-hover:bg-[#F8F8F8] align-top"
                                                                                style={{ width: columnWidth["Control Name"], maxWidth: columnWidth["Control Name"] }}
                                                                            >
                                                                                <div className="flex flex-col break-words space-y-1">
                                                                                    <span className="font-medium text-gray-800">{cell.render("Cell")}</span>
                                                                                    {row.original.controlDefinition && (
                                                                                        <span className="text-xs text-gray-500 line-clamp-2">{row.original.controlDefinition}</span>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        );
                                                                    }
                                                                    
                                                                    if (cell.column.Header === "Type" || cell.column.id === "controlType") {
                                                                        return (
                                                                            <td
                                                                                {...cell.getCellProps()}
                                                                                className="border-t border-gray-200 px-3 py-2 group-hover:bg-[#F8F8F8] whitespace-nowrap text-center"
                                                                                style={{ width: columnWidth["Type"] }}
                                                                            >
                                                                                {cell.render("Cell")}
                                                                            </td>
                                                                        );
                                                                    }
                                                                    
                                                                    if (cell.column.Header === "Owner" || cell.column.id === "owner") {
                                                                        return (
                                                                            <td
                                                                                {...cell.getCellProps()}
                                                                                className={`overflow-hidden text-ellipsis whitespace-nowrap border-t border-gray-200 px-3 py-2 group-hover:bg-[#F8F8F8] font-semibold text-gray-900 ${centerColumns.includes(cell.column.Header) ? "text-center" : "text-left"}`}
                                                                                style={{ width: columnWidth[cell.column.Header] }}
                                                                            >
                                                                                {cell.render("Cell")}
                                                                            </td>
                                                                        );
                                                                    }
                                                                    
                                                                    if (cell.column.Header === "Period" || cell.column.id === "controlPeriod" || cell.column.id === "period") {
                                                                        return (
                                                                            <td
                                                                                {...cell.getCellProps()}
                                                                                className={`border-t border-gray-200 px-3 py-2 group-hover:bg-[#F8F8F8] whitespace-nowrap ${centerColumns.includes(cell.column.Header) ? "text-center" : "text-left"}`}
                                                                                style={{ width: columnWidth[cell.column.Header] }}
                                                                            >
                                                                                {cell.value ? (
                                                                                    <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-medium inline-block">
                                                                                    {cell.render("Cell")}
                                                                                    </span>
                                                                                ) : (
                                                                                    cell.render("Cell")
                                                                                )}
                                                                            </td>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <td
                                                                            {...cell.getCellProps()}
                                                                            className={`overflow-hidden text-ellipsis whitespace-nowrap border-t border-gray-200 px-3 py-2 group-hover:bg-[#F8F8F8] ${centerColumns.includes(cell.column.Header) ? "text-center" : "text-left"}`}
                                                                            style={{ width: columnWidth[cell.column.Header] }}
                                                                        >
                                                                            {cell.render("Cell")}
                                                                        </td>
                                                                    );
                                                                };
                                                                
                                                                const order = ["Control Name", "Type", "Frameworks", "Findings", "Effectiveness", "Owner", "Period"];
                                                                return order.map((name) => {
                                                                    if (name === "Findings") {
                                                                        const num = Number(row.original.findingNumber ?? 0);
                                                                        const cls = isNaN(num) ? "" : (num === 0 ? "badge-green" : "badge-red");
                                                                        return (
                                                                            <td
                                                                                key="Findings"
                                                                                className="border-t border-gray-200 px-2 py-2 group-hover:bg-[#F8F8F8] whitespace-nowrap text-center"
                                                                                style={{ width: columnWidth["Findings"] }}
                                                                            >
                                                                                {isNaN(num) ? "-" : <span className={cls}>{num}</span>}
                                                                            </td>
                                                                        );
                                                                    }
                                                                    return renderCell(cellMap[name]);
                                                                });
                                                            })()}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {renderPagination()}
                                </>
                            )}
                        </>
                    )}

                    <Popup
                        open={isControlOpen}
                        onClose={() => setIsControlOpen(false)}
                        contentStyle={{ overflow: 'auto', maxHeight: '80vh', width: '900px', marginTop: '14vh' }}
                    >
                        <ControlOverlay key={control_id || 'new-control'} closeControl={closeCreation} control_id={control_id} risk_id={risk_id} />
                    </Popup>

                    </div>
                </div>
            </div>
        </div>
    ) : (
        <Redirect to="/login" />
    );
}

const mapStateToProps = (state) => {
    console.log("Controls:", state.risk.risk_controls)
    return {
        token: state.auth.token,
        controls: state.risk.all_risk_controls,
        activeDepartmentId: state.department.activeDepartmentId,
    }
};

const mapDispatchToProps = (dispatch) => ({
    getControlsReportFN: (departmentId) => dispatch(getControls_Report(departmentId)),
    getControlsSearchFN: (departmentId) => dispatch(getControls_Search(departmentId)),
    editControlFN: (risk_id, control_id, control) => dispatch(editControl(risk_id, control_id, control)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(AllControls);
